import { createServerFn } from "@tanstack/react-start";
import { env } from "cloudflare:workers";
import { createDb } from "@/lib/db";
import { getServerSession } from "@/lib/session";
import { emailConversationIndex } from "@/db/schema";
import type { EmailMessage, EmailAgentState } from "@/server/email-agent";
import { eq, sql } from "drizzle-orm";
import { nanoid } from "nanoid";
import * as Sentry from "@sentry/cloudflare";

async function requireAdmin() {
  const session = await getServerSession();
  if (!session?.user || session.user.role !== "admin") {
    throw new Error("Forbidden");
  }
  return session;
}

export type ConversationListItem = {
  id: string;
  fromEmail: string;
  fromName: string | null;
  subject: string;
  status: string;
  messageCount: number;
  lastMessageAt: Date | string | number;
  createdAt: Date | string | number;
};

export type ConversationListResponse = {
  data: ConversationListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

export const getAdminEmailConversations = createServerFn({ method: "GET" })
  .inputValidator(
    (data: { status: string; page: number; limit?: number }) => data,
  )
  .handler(async ({ data }): Promise<ConversationListResponse> => {
    await requireAdmin();

    const { status, page, limit: inputLimit } = data;
    const limit = inputLimit ?? 20;
    const offset = (page - 1) * limit;

    try {
      const db = createDb(env.DB);

      let query = db
        .select()
        .from(emailConversationIndex)
        .orderBy(sql`${emailConversationIndex.lastMessageAt} DESC`)
        .limit(limit)
        .offset(offset);

      if (status !== "all") {
        query = query.where(
          eq(emailConversationIndex.status, status),
        ) as any;
      }

      const conversations = await query;

      const countResult = await db
        .select({ count: sql<number>`count(*)` })
        .from(emailConversationIndex)
        .where(
          status !== "all"
            ? eq(emailConversationIndex.status, status)
            : undefined,
        )
        .get();

      const total = countResult?.count || 0;

      return {
        data: conversations as ConversationListItem[],
        pagination: {
          page,
          limit,
          total,
          totalPages: Math.ceil(total / limit),
        },
      };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: "admin-email-conversations" },
        level: "error",
      });
      console.error("[getAdminEmailConversations] Exception:", error);
      throw error;
    }
  });

export const getAdminEmailThread = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<EmailAgentState> => {
    await requireAdmin();

    try {
      const stub = env.EmailAgent.get(env.EmailAgent.idFromName(data.id));
      const thread = await stub.getThread();
      return thread;
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: "admin-email-thread", id: data.id },
        level: "error",
      });
      console.error("[getAdminEmailThread] Exception:", error);
      throw error;
    }
  });

export const sendAdminEmailReply = createServerFn({ method: "POST" })
  .inputValidator((data: { id: string; body: string }) => data)
  .handler(async ({ data }) => {
    await requireAdmin();

    const { id, body } = data;

    try {
      const stub = env.EmailAgent.get(env.EmailAgent.idFromName(id));
      const thread = await stub.getThread();

      if (!thread.fromEmail) {
        throw new Error("No recipient found");
      }

      const replySubject = thread.subject.startsWith("Re:")
        ? thread.subject
        : `Re: ${thread.subject}`;

      // Queue email for sending
      await env.SENDEMAILQUEUE.send({
        type: "raw-email",
        to: thread.fromEmail,
        from: "support@images-ai.app",
        subject: replySubject,
        body,
        conversationId: id,
      });

      // Add outbound message to DO state
      const outboundMessage: EmailMessage = {
        id: nanoid(),
        direction: "outbound",
        fromEmail: "support@images-ai.app",
        toEmail: thread.fromEmail,
        subject: replySubject,
        bodyText: body,
        bodyHtml: null,
        messageId: null,
        inReplyTo: null,
        isAutoReply: false,
        aiDraft: null,
        aiConfidence: null,
        createdAt: Date.now(),
      };

      await stub.addOutboundMessage(outboundMessage);

      // Update D1 index
      const db = createDb(env.DB);
      await db
        .update(emailConversationIndex)
        .set({
          status: "closed",
          messageCount: thread.messages.length + 1,
          lastMessageAt: new Date(),
          updatedAt: new Date(),
        })
        .where(eq(emailConversationIndex.id, id));

      return { success: true };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: "admin-email-reply", id },
        level: "error",
      });
      console.error("[sendAdminEmailReply] Exception:", error);
      throw error;
    }
  });

export const composeAdminEmail = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { to: string; subject: string; body: string }) => data,
  )
  .handler(async ({ data }) => {
    await requireAdmin();

    const { to, subject, body } = data;
    const conversationId = nanoid();

    try {
      // Queue email for sending
      await env.SENDEMAILQUEUE.send({
        type: "raw-email",
        to,
        from: "support@images-ai.app",
        subject,
        body,
        conversationId,
      });

      // Create D1 index entry
      const db = createDb(env.DB);
      const now = new Date();
      await db.insert(emailConversationIndex).values({
        id: conversationId,
        fromEmail: "support@images-ai.app",
        fromName: "Images AI Support",
        subject,
        status: "closed",
        userId: null,
        messageCount: 1,
        lastMessageAt: now,
        createdAt: now,
        updatedAt: now,
      });

      // Create DO state with the outbound message
      const stub = env.EmailAgent.get(
        env.EmailAgent.idFromName(conversationId),
      );
      await stub.addOutboundMessage({
        id: nanoid(),
        direction: "outbound",
        fromEmail: "support@images-ai.app",
        toEmail: to,
        subject,
        bodyText: body,
        bodyHtml: null,
        messageId: null,
        inReplyTo: null,
        isAutoReply: false,
        aiDraft: null,
        aiConfidence: null,
        createdAt: Date.now(),
      });

      return { success: true, conversationId };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: "admin-email-compose" },
        level: "error",
      });
      console.error("[composeAdminEmail] Exception:", error);
      throw error;
    }
  });

export const updateAdminEmailStatus = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      id: string;
      status: "open" | "auto_replied" | "needs_review" | "closed";
    }) => data,
  )
  .handler(async ({ data }) => {
    await requireAdmin();

    const { id, status } = data;

    try {
      // Update DO state
      const stub = env.EmailAgent.get(env.EmailAgent.idFromName(id));
      await stub.updateStatus(status);

      // Update D1 index
      const db = createDb(env.DB);
      await db
        .update(emailConversationIndex)
        .set({ status, updatedAt: new Date() })
        .where(eq(emailConversationIndex.id, id));

      return { success: true };
    } catch (error) {
      Sentry.captureException(error, {
        extra: { operation: "admin-email-status", id },
        level: "error",
      });
      console.error("[updateAdminEmailStatus] Exception:", error);
      throw error;
    }
  });
